/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/** @sfdc-extension-file SFDC_EXT_SF_PAYMENTS */

'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSite, resolvePrefix } from '@salesforce/storefront-next-runtime/site-context';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { ShopperBasketsV2 } from '@/scapi';
import { useSFPaymentsContext } from '../../providers/sf-payments-provider';
import { useSFPaymentsConfig } from '../../hooks/use-sf-payments-config';
import { useShopperConfigField, SHOPPER_CONFIG_DEFAULTS } from '../../hooks/use-sf-payments-shopper-config';
import { buildPaymentProcessingUrl, EXPRESS_PAY_NOW, isPayPalPaymentMethodType } from '../../lib/sf-payments-utils';
import { buildTheme } from '../../lib/sf-payments-theme';
import { useExpressCallbacks, type SdkExpressOptions } from './use-express-callbacks';
import ExpressProcessingOverlay from '../express-processing-overlay';
import { cn } from '@/lib/utils';

type Basket = ShopperBasketsV2.schemas['Basket'];

export interface ExpressButtonsProps {
    /** Async function that returns a basket to use for the express payment flow.
     *  On PDP: creates a temporary basket and adds the current product.
     *  On checkout/cart: returns the existing main basket. */
    prepareBasket: () => Promise<Basket>;
    /** Display amount before prepareBasket resolves (product price on PDP, basket total on checkout) */
    initialAmount: number;
    /** Transaction currency for this payment — sourced by the consumer from
     *  basket/product/site as appropriate. */
    currency: string;
    /** Transaction country code for this payment — sourced by the consumer from
     *  billing address or a locale-derived fallback. */
    countryCode: string;
    /** EXPRESS_PAY_NOW (0, default) for checkout/cart, EXPRESS_BUY_NOW (1) for PDP */
    usage?: number;
    expressButtonLayout?: 'horizontal' | 'vertical';
    maximumButtonCount?: number;
    /** Called when SDK confirms which payment methods rendered — useful for conditional
     *  UI such as showing/hiding an "Express Checkout" heading on the checkout page. */
    // RGBARRY-CUSTOMIZATION: expose the SDK's actual rendered wallet set for stable cart previews.
    onPaymentMethodsRendered?: (paymentMethods: unknown[]) => void;
    // END RGBARRY-CUSTOMIZATION
    /** Called after payment completes, before navigation — useful for closing modals/drawers
     *  (e.g. mini-cart). */
    onExpressPaymentCompleted?: () => void;
    /** Called after a cancelled main-basket express attempt has cleared its shipping address. */
    onExpressPaymentCancelled?: () => void;
    /** CSS class applied to the outer container div */
    className?: string;
    /** Submit create-order via plain `fetch` instead of `useFetcher`, bypassing
     *  React Router's automatic post-action loader revalidation. Set on surfaces
     *  (e.g. cart) whose loader returns an empty basket after order creation and
     *  would otherwise tear down this subtree mid-flow. */
    skipPostOrderRevalidation?: boolean;
    /** Drop PayPal/Venmo payment methods before handing the set to the SDK, so this
     *  instance renders only the remaining Salesforce-owned wallets. Set on surfaces
     *  where PayPal is owned by another provider (e.g. cart, where Cybersource renders
     *  its own PayPal express button alongside these wallets) to avoid a duplicate button. */
    excludePayPal?: boolean;
    // RGBARRY-CUSTOMIZATION: allow cart to reserve wallet-button space until the async SDK paints.
    /** Optional content that reserves the express-button footprint while the SDK initializes.
     *  A render function receives the configured express-method count once configuration loads;
     *  before then the count is undefined. Pass `null` to intentionally render no loading UI. */
    loadingFallback?: ReactNode | ((buttonCount?: number) => ReactNode);
}

// RGBARRY-CUSTOMIZATION: size the loading footprint from configured express methods only.
function isExpressPaymentMethodType(paymentMethodType?: string) {
    const normalizedType = paymentMethodType?.toLowerCase();
    return ['applepay', 'googlepay', 'paypal', 'venmo'].includes(normalizedType ?? '');
}

/**
 * Express buttons — renders eligible wallet buttons via the SF Payments SDK.
 *
 * Consumes two hooks:
 *   - `useSFPaymentsContext()` for SDK-lifecycle state (enabled, metadata, sfp).
 *   - `useSFPaymentsConfig({ currency, countryCode })` for per-transaction
 *     payment configuration (gateway accounts, payment methods).
 *
 * `paymentRequest` values (currency, country, locale) come from props + site
 * context. The component does not read transaction values from provider
 * context — that separation lets multiple concurrent consumers use different
 * currencies/countries without stale-cache surprises.
 */
export default function ExpressButtons(props: ExpressButtonsProps) {
    const {
        currency,
        countryCode,
        usage = EXPRESS_PAY_NOW,
        expressButtonLayout,
        maximumButtonCount,
        excludePayPal,
        loadingFallback: loadingFallbackProp,
    } = props;

    const { enabled, sfp, metadata, metadataLoading } = useSFPaymentsContext();
    const [isExpressProcessing, setIsExpressProcessing] = useState(false);
    const startExpressProcessing = useCallback(() => setIsExpressProcessing(true), []);
    const endExpressProcessing = useCallback(() => setIsExpressProcessing(false), []);
    // RGBARRY-CUSTOMIZATION: the SDK mounts asynchronously (script load + metadata + config),
    // so the button container can sit empty for a moment after the surrounding loading states
    // clear. Track the SDK's own "rendered" signal so the buttons fade in once they're actually
    // there, instead of popping in abruptly.
    const [buttonsRendered, setButtonsRendered] = useState(false);
    // RGBARRY-CUSTOMIZATION: retain the rendered method payload instead of reducing it to a boolean signal.
    const handlePaymentMethodsRendered = useCallback(
        (paymentMethods: unknown[]) => {
            setButtonsRendered(true);
            props.onPaymentMethodsRendered?.(paymentMethods);
        },
        [props]
    );
    // END RGBARRY-CUSTOMIZATION
    const propsWithRenderTracking = { ...props, onPaymentMethodsRendered: handlePaymentMethodsRendered };
    const {
        config: paymentConfig,
        loading: paymentConfigLoading,
        error: paymentConfigError,
    } = useSFPaymentsConfig({ currency, countryCode });
    // The set of methods this instance will actually hand to the SDK. `excludePayPal` drops
    // PayPal/Venmo (owned by another provider on cart/checkout). The config hook normalizes
    // `paymentMethods` to an array, so this is safe even when the backend returns none. Used both
    // to gate the SDK bind (effect) and to decide whether to render at all (below).
    const renderableMethods = useMemo(() => {
        const methods = paymentConfig?.paymentMethods ?? [];
        // RGBARRY-CUSTOMIZATION: Cybersource's native SFCC bridge exclusively owns Apple Pay.
        // Enforce this before every SF Payments SDK bind, independent of surface or shopper auth.
        return methods.filter((pm) => {
            if (excludePayPal && isPayPalPaymentMethodType(pm.paymentMethodType)) return false;
            if (pm.paymentMethodType?.toLowerCase() === 'applepay') return false;
            return true;
        });
        // END RGBARRY-CUSTOMIZATION
    }, [paymentConfig, excludePayPal]);
    // RGBARRY-CUSTOMIZATION: reconcile the fallback count after Salesforce returns enabled methods.
    const renderableExpressMethodCount = useMemo(
        () => renderableMethods.filter((method) => isExpressPaymentMethodType(method.paymentMethodType)).length,
        [renderableMethods]
    );
    const loadingFallback = useMemo(() => {
        if (!loadingFallbackProp) return null;
        return typeof loadingFallbackProp === 'function'
            ? loadingFallbackProp(paymentConfig ? renderableExpressMethodCount : undefined)
            : loadingFallbackProp;
    }, [loadingFallbackProp, paymentConfig, renderableExpressMethodCount]);
    // Defer SDK initialization until shopper config is loaded — binding during the
    // loading window risks a fast-click flow running with the wrong capture mode.
    const { value: cardCaptureAutomatic, loading: shopperConfigLoading } = useShopperConfigField(
        'cardCaptureAutomatic',
        SHOPPER_CONFIG_DEFAULTS.cardCaptureAutomatic
    );
    const { site, locale } = useSite();
    const localeId = locale.id;
    const appConfig = useConfig<{ url?: { prefix?: string } }>();
    const sitePrefix = useMemo(
        () => resolvePrefix({ prefix: appConfig.url?.prefix ?? '', params: { siteId: site.id, localeId } }),
        [appConfig.url?.prefix, site.id, localeId]
    );
    // Redirect-capable methods (Adyen redirect flows) return to this URL after
    // auth. Sheet does the same in payment-methods/index.tsx.
    const baseUrl = typeof window !== 'undefined' ? window.location.origin + sitePrefix : sitePrefix;

    const containerRef = useRef<HTMLDivElement | null>(null);
    // Mutable options object passed to sfp.express.
    const sdkExpressOptionsRef = useRef<SdkExpressOptions | null>(null);

    // The SF Payments SDK measures the container width when `sfp.express()` runs and only recomputes
    // button widths on a later window resize — so if it binds before the surrounding layout has
    // settled (common on wide viewports) the buttons latch a stale, too-narrow width until the
    // shopper resizes. Track the container's settled width via a ResizeObserver, defer the SDK bind
    // until we have it (gated in the mount effect), and re-bind when the width actually changes.
    const [containerWidth, setContainerWidth] = useState(0);
    const containerWidthRef = useRef(0);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const resizeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const setContainerNode = useCallback((node: HTMLDivElement | null) => {
        containerRef.current = node;
        resizeObserverRef.current?.disconnect();
        resizeObserverRef.current = null;
        if (resizeDebounceRef.current) {
            clearTimeout(resizeDebounceRef.current);
            resizeDebounceRef.current = null;
        }
        if (!node) {
            return;
        }
        if (typeof ResizeObserver === 'undefined') {
            // No ResizeObserver (older browsers / non-DOM env): bind at the current measured width.
            const width = Math.round(node.getBoundingClientRect().width) || 1;
            containerWidthRef.current = width;
            setContainerWidth(width);
            return;
        }
        const observer = new ResizeObserver((entries) => {
            const width = Math.round(entries[0]?.contentRect.width ?? 0);
            if (!width || width === containerWidthRef.current) {
                return;
            }
            if (containerWidthRef.current === 0) {
                // First real measurement, taken after layout settles: bind immediately.
                containerWidthRef.current = width;
                setContainerWidth(width);
                return;
            }
            // Later width changes (resize / responsive breakpoint cross): debounce a single re-bind
            // so an active drag-resize doesn't thrash the SDK on every animation frame.
            if (resizeDebounceRef.current) {
                clearTimeout(resizeDebounceRef.current);
            }
            resizeDebounceRef.current = setTimeout(() => {
                containerWidthRef.current = width;
                setContainerWidth(width);
            }, 150);
        });
        observer.observe(node);
        resizeObserverRef.current = observer;
    }, []);

    const { callbacks, eventHandlers, isPaymentInProgress, expressComponentRef } = useExpressCallbacks({
        props: propsWithRenderTracking,
        paymentConfig,
        sdkExpressOptionsRef,
        startExpressProcessing,
        endExpressProcessing,
        baseUrl,
    });

    useEffect(() => {
        if (!sfp || !metadata || !paymentConfig || shopperConfigLoading || !containerRef.current || !containerWidth) {
            return;
        }

        if (isPaymentInProgress.current && expressComponentRef.current) {
            return;
        }

        if (expressComponentRef.current) {
            expressComponentRef.current.destroy();
            expressComponentRef.current = null;
        }

        // Re-mounting (e.g. a variant/currency change on PDP) should fade the buttons back
        // in rather than leaving stale content visible under the old animation state.
        setButtonsRendered(false);

        // `renderableMethods` has already applied `excludePayPal` (drop PayPal/Venmo so the SDK
        // renders only wallet buttons and we don't paint a second PayPal next to Cybersource's).
        // If it's empty — Salesforce Payments is enabled but the instance's backend returned no
        // usable methods (e.g. the merchant isn't connected) — don't bind the SDK. The render path
        // also returns `null` below, matching the PDP surface. This is what breaks the cart/checkout
        // crash-loop too: the old `.filter` on an undefined method list threw on every bind, and the
        // error-boundary remount re-ran the effect.
        if (renderableMethods.length === 0) {
            return;
        }

        const paymentMethodSet = {
            paymentMethods: renderableMethods,
            paymentMethodSetAccounts: paymentConfig.paymentMethodSetAccounts ?? [],
        };

        const expressOptions: SdkExpressOptions = {
            shippingAddressRequired: true,
            emailAddressRequired: true,
            billingAddressRequired: true,
            phoneNumberRequired: true,
            useManualCapture: !cardCaptureAutomatic,
            maximumButtonCount,
            returnUrl: buildPaymentProcessingUrl({ origin: baseUrl }),
        };
        sdkExpressOptionsRef.current = expressOptions;

        const config = {
            actions: callbacks,
            options: expressOptions,
            theme: buildTheme({ expressButtonLayout }),
        };

        const paymentRequest = {
            amount: props.initialAmount,
            currency,
            country: countryCode,
            locale: localeId,
        };
        const paymentElement = document.createElement('div');
        containerRef.current.replaceChildren(paymentElement);

        paymentElement.addEventListener('sfp:paymentmethodsrendered', eventHandlers.onPaymentMethodsRendered);
        paymentElement.addEventListener('sfp:paymenterror', eventHandlers.onPaymentError);
        paymentElement.addEventListener('sfp:paymentcancel', eventHandlers.onCancel);
        paymentElement.addEventListener('sfp:paymentapprove', eventHandlers.onPaymentApprove);

        expressComponentRef.current = sfp.express(
            metadata,
            paymentMethodSet,
            config,
            paymentRequest,
            paymentElement,
            usage
        );

        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            if (!isPaymentInProgress.current) {
                expressComponentRef.current?.destroy();
                expressComponentRef.current = null;
            }
        };
        // `shopperConfigLoading` is what actually triggers the bind — it
        // flips false once we know the merchant's capture mode.
        // `cardCaptureAutomatic` is listed because the effect reads it
        // (eslint requires it); both values change together so it doesn't
        // cause extra re-binds. `containerWidth` gates the initial bind on a
        // settled container width and re-binds on resize; `expressButtonLayout`
        // re-binds when the responsive layout flips horizontal <-> vertical.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        sfp,
        metadata,
        paymentConfig,
        renderableMethods,
        shopperConfigLoading,
        cardCaptureAutomatic,
        containerWidth,
        expressButtonLayout,
    ]);

    if (!enabled && !paymentConfigLoading) {
        return null;
    }

    if (paymentConfigLoading || metadataLoading || shopperConfigLoading) {
        // RGBARRY-CUSTOMIZATION: reserve the final button footprint during configuration and SDK loading.
        // An explicitly-null fallback suppresses the default loading copy on surfaces such as PDP.
        if (loadingFallbackProp !== undefined) {
            return loadingFallback;
        }
        // TODO: pending translation
        return <div className="py-4 text-center text-sm text-muted-foreground">Loading payment options...</div>;
    }

    if (paymentConfigError) {
        // TODO: pending translation
        return (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm text-center">
                {paymentConfigError}
            </div>
        );
    }

    // Enabled and loaded, but this instance has no methods to render — the backend isn't
    // configured, or it's a PayPal-only config under `excludePayPal`. Render nothing rather than an
    // empty container so the surrounding layout collapses: on checkout the nested Cybersource
    // PayPal then takes the full express row instead of the wallets leaving an empty two-thirds gap.
    if (renderableMethods.length === 0) {
        return null;
    }

    // PayPal's button iframes use high z-indexes and don't create their own
    // stacking context, so they can paint over overlays like the mini-cart.
    // `isolate` scopes them by giving this container its own stacking context.
    // RGBARRY-CUSTOMIZATION: when a preview reserves the button footprint, keep the SDK layer
    // absolutely overlaid for its entire lifetime. The swap is opacity-only, so the rendered event
    // cannot move either layer into/out of document flow and shake the surrounding cart UI.
    const containerClass = cn(
        'isolate',
        loadingFallback && 'absolute inset-0 transition-opacity duration-200 ease-out motion-reduce:transition-none',
        loadingFallback && (buttonsRendered ? 'opacity-100' : 'pointer-events-none opacity-0'),
        !loadingFallback && props.className,
        !loadingFallback && buttonsRendered && 'motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200'
    );
    // END RGBARRY-CUSTOMIZATION

    const expressContainer = (
        <div
            ref={setContainerNode}
            className={containerClass}
            aria-hidden={loadingFallback && !buttonsRendered ? true : undefined}
            data-testid="sf-payments-express"
        />
    );

    return (
        <>
            {/* RGBARRY-CUSTOMIZATION: the preview remains mounted as the permanent sizing layer while
                the SDK crossfades above it. Keeping both DOM layers stable prevents a render-time reflow. */}
            {loadingFallback ? (
                <div className={cn('relative isolate', props.className)} data-testid="sf-payments-express-shell">
                    <div
                        aria-hidden="true"
                        className={cn(
                            'transition-opacity duration-200 ease-out motion-reduce:transition-none',
                            buttonsRendered ? 'pointer-events-none opacity-0' : 'opacity-100'
                        )}
                        data-testid="sf-payments-express-fallback">
                        {loadingFallback}
                    </div>
                    {expressContainer}
                </div>
            ) : (
                expressContainer
            )}
            {/* END RGBARRY-CUSTOMIZATION */}
            <ExpressProcessingOverlay isProcessing={isExpressProcessing} />
        </>
    );
}
